import { router } from '@inertiajs/react'
import { useState } from 'react'
import { SearchIcon, XIcon, TruckIcon, WrenchIcon, DollarSignIcon, UsersIcon, TrendingUpIcon } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

interface Call {
  id: number
  direction: string | null
  from_number: string | null
  to_number: string | null
  employee_name: string | null
  time_chicago: string
  filename: string
  duration: number | null
  duration_formatted: string
  department: string | null
  call_category: string | null
  call_type: string | null
  caller_type: string | null
  load_source: string | null
  load_type: string | null
  broker_name: string | null
  lane: string | null
  rate_per_mile: number | null
  total_rate: number | null
  load_status: string | null
  repair_type: string | null
  truck_id: string | null
  repair_status: string | null
  repair_estimate: number | null
  parts_needed: string | null
  eta_completion: string | null
  revenue_discussed: boolean | null
  weekly_gross: number | null
  dispatch_fee: number | null
  competitor_mentioned: boolean | null
  objection: string | null
  deal_status: string | null
  action_promised: string | null
  action_deadline: string | null
  callback_required: boolean | null
  callback_time: string | null
  documents_to_send: string | null
  escalation_needed: boolean | null
  professionalism_score: number | null
  call_handled_well: boolean | null
  missed_opportunity: boolean | null
  caller_sentiment: string | null
  issue_resolved: boolean | null
  topic: string | null
  transcript_language: string | null
  raw_transcript: string | null
  audio_url: string | null
}

interface KPIs {
  total_calls: number
  avg_professionalism: number
  dispatch_calls: number
  fleet_calls: number
  accounting_calls: number
  hr_calls: number
  sales_calls: number
}

interface Filters {
  search?: string | null
}

interface CallsIndexProps {
  calls: Call[]
  kpis: KPIs
  filters: Filters
  pagy: {
    count: number
    page: number
    items: number
    pages: number
    last: number
    offset: number
    from: number
    to: number
    prev: number | null
    next: number | null
  }
}

export default function CallsIndex({ calls, kpis, filters, pagy }: CallsIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '')

  const handleSearch = () => {
    router.get('/calls', { search: searchTerm }, { preserveState: true })
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    router.get('/calls', {}, { preserveState: true })
  }

  const getDepartmentColor = (department: string | null) => {
    if (!department) return 'secondary'
    switch (department) {
      case 'Dispatch':
        return 'default' // Blue
      case 'Fleet & Maintenance':
        return 'destructive' // Orange/Red
      case 'HR & Recruiting':
        return 'outline' // Purple-ish
      case 'Accounting':
        return 'default' // Green-ish
      case 'Sales':
        return 'secondary' // Pink-ish
      default:
        return 'secondary' // Gray
    }
  }

  const getCallerTypeColor = (callerType: string | null) => {
    if (!callerType) return 'secondary'
    if (callerType.includes('Driver')) return 'default'
    if (callerType === 'Broker') return 'outline'
    if (callerType === 'New Lead') return 'default'
    return 'secondary'
  }

  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams()
    if (filters.search) params.set('search', filters.search)
    params.set('page', page.toString())
    return `/calls?${params.toString()}`
  }

  const renderPagination = () => {
    if (pagy.pages <= 1) return null

    const pages = []
    const maxPagesToShow = 5
    let startPage = Math.max(1, pagy.page - Math.floor(maxPagesToShow / 2))
    let endPage = Math.min(pagy.pages, startPage + maxPagesToShow - 1)

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return (
      <div className="mt-6">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              {pagy.prev ? (
                <PaginationPrevious href={buildPageUrl(pagy.page - 1)} />
              ) : (
                <PaginationPrevious
                  className="pointer-events-none opacity-50"
                  aria-disabled="true"
                />
              )}
            </PaginationItem>

            {startPage > 1 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}

            {pages.map((page) => {
              const isActive = page === pagy.page

              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    href={buildPageUrl(page)}
                    isActive={isActive}
                    className={isActive ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )
            })}

            {endPage < pagy.pages && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}

            <PaginationItem>
              {pagy.next ? (
                <PaginationNext href={buildPageUrl(pagy.page + 1)} />
              ) : (
                <PaginationNext
                  className="pointer-events-none opacity-50"
                  aria-disabled="true"
                />
              )}
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight">Calls</h1>
              <p className="text-muted-foreground">
                View and analyze call records from 3CX phone system
              </p>
            </div>

            {/* KPI Cards */}
            <div className="mb-6 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Calls
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.total_calls}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Avg. Professionalism
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.avg_professionalism} / 10</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TruckIcon className="h-4 w-4" />
                    Dispatch
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.dispatch_calls}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <WrenchIcon className="h-4 w-4" />
                    Fleet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.fleet_calls}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <DollarSignIcon className="h-4 w-4" />
                    Accounting
                  </CardTitle>
                </CardHeader>
<CardContent>
                  <div className="text-2xl font-bold">{kpis.accounting_calls}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" />
                    HR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.hr_calls}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TrendingUpIcon className="h-4 w-4" />
                    Sales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.sales_calls}</div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-end">
                  <div className="flex-1">
                    <label className="mb-2 block text-sm font-medium">Search</label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search by employee, broker, truck ID, department..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <Button onClick={handleSearch} data-testid="search-button">
                        <SearchIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {(filters.search) && (
                    <Button
                      variant="outline"
                      onClick={handleClearFilters}
                      data-testid="clear-filters-button"
                    >
                      <XIcon className="mr-2 h-4 w-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Calls Table */}
            <Card>
              <CardContent className="p-0">
                <div className="min-w-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[160px]">Time (CT)</TableHead>
                        <TableHead className="min-w-[180px]">Employee</TableHead>
                        <TableHead className="min-w-[200px]">Filename</TableHead>
                        <TableHead className="min-w-[140px]">Department</TableHead>
                        <TableHead className="min-w-[140px]">Call Type</TableHead>
                        <TableHead className="min-w-[180px]">Caller Type</TableHead>
                        <TableHead className="min-w-[120px]">Prof. Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calls.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            No calls found
                          </TableCell>
                        </TableRow>
                      ) : (
                        calls.map((call) => (
                          <TableRow
                            key={call.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => router.visit(`/calls/${call.id}`)}
                          >
                            <TableCell className="font-medium">{call.time_chicago}</TableCell>
                            <TableCell className="font-medium">
                              {call.employee_name || '-'}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate font-medium text-primary" title={call.filename}>
                              {call.filename}
                            </TableCell>
                            <TableCell>
                              {call.department ? (
                                <Badge variant={getDepartmentColor(call.department)}>
                                  {call.department}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {call.call_type ? (
                                <Badge variant="outline">{call.call_type}</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {call.caller_type ? (
                                <Badge variant={getCallerTypeColor(call.caller_type)}>
                                  {call.caller_type}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {call.professionalism_score !== null ? (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{call.professionalism_score}</span>
                                  <span className="text-muted-foreground">/ 10</span>
                                </div>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination Info */}
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Showing {pagy.from} to {pagy.to} of {pagy.count} calls
            </div>

            {/* Pagination Controls */}
            {renderPagination()}

            {/* Footnote */}
            <div className="mt-6 text-sm text-muted-foreground">
              <p>
                <strong>Note:</strong> All times shown in Chicago CT timezone. Department
                distribution helps identify which business units generate most calls. Professionalism
                scores range from 0-10 based on AI analysis of call quality.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
